import { Component, inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  ButtonComponent,
  FormSectionComponent,
  TextFieldWithErrorComponent
} from 'htx-ui-library';
import { FormErrorMessagesService } from '../../../../../services/form/form-error-messages.service';
import { BdrsEntry, BdrsMappingRequest } from '../../../../../services/infrastructure/infrastructure.model';

/** The dataspace agreed on did:web identifiers — a BDRS DID must start with this prefix. */
export const DID_WEB_PREFIX = 'did:web:';

export function didWebValidator(control: AbstractControl): ValidationErrors | null {
  const value: string = control.value ?? '';
  if (value.length === 0) {
    return null;
  }
  return value.startsWith(DID_WEB_PREFIX) ? null : { didWeb: true };
}

export interface BdrsFormDialogData {
  mode: 'add' | 'edit';
  entry?: BdrsEntry;
}

@Component({
  selector: 'app-bdrs-form-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    TranslocoDirective,
    ButtonComponent,
    FormSectionComponent,
    TextFieldWithErrorComponent
  ],
  templateUrl: './bdrs-form-dialog.component.html',
  styleUrl: './bdrs-form-dialog.component.scss'
})
export class BdrsFormDialogComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dialogRef = inject(MatDialogRef<BdrsFormDialogComponent, BdrsMappingRequest>);
  readonly data = inject<BdrsFormDialogData>(MAT_DIALOG_DATA);
  readonly errorMessagesService = inject(FormErrorMessagesService);

  readonly isEdit = this.data.mode === 'edit';

  form = this.fb.group({
    bpn: ['BPNL', [Validators.required, Validators.maxLength(50)]],
    did: [DID_WEB_PREFIX, [Validators.required, didWebValidator, Validators.maxLength(512)]]
  });

  ngOnInit(): void {
    const entry = this.data.entry;
    if (entry) {
      this.form.patchValue({ bpn: entry.bpn, did: entry.did });
    }
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.getRawValue();
    this.dialogRef.close({
      bpn: (value.bpn ?? '').trim(),
      did: (value.did ?? '').trim()
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
